import React, { useEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@mui/styles'

import ClientConfig from '@/modules/client/config'
import addElementToRefs from '@/modules/_helpers/addElementToRefs'
import lang from '@/lang'

import Topbar from '@/modules/base/components/topbar/topbar'
import Subnavigation from '@/modules/base/components/sub-navigation'
import ClientOverview from '@/modules/client/components/detail/client-overview-widget'
import ClientTodo from '@/modules/client/components/detail/client-todo-widget'
import ClientESigning from '@/modules/client/components/detail/client-eSigning-widget'
import ClientTaxForm from '@/modules/client/components/detail/client-tax-form-widget'
import useClientSetting from '@/modules/client/_helpers/useClientSetting'
import ClientDatevExport from '@/modules/client/components/detail/client-datev-export-widget'
import ClientDocumentExport from '@/modules/client/components/detail/client-document-export-widget'
import ClientCreatedExports from '@/modules/client/components/detail/client-created-exports-widget'
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import AccountSettingProvider from '@/modules/settings/components/tax-settings/services/account-setting-provider'
import { CLIENT_INVITATION_STATUS } from '@/modules/client/_helpers/getStatusLabel'
import { SUBSCRIPTION_MODULES } from '@/modules/base/util/constants'
import useScrollToJumpmarks from '@/modules/_helpers/useScrollToJumpmarks'
import { withAsyncData } from '@/utils/withAsyncData'
import { dispatch } from '@/redux/store'
import { fetchClientData } from '@/redux/ducks/client/detail'
import Loader from '@/modules/base/components/loader/loader'

const { clientDetail: { jumpMarkLinks } } = ClientConfig

const useStyles = makeStyles((theme) => ({
  clientDetailWrapper: {
    width: '100%',
  },
  clientContentWrapper: {
    display: 'flex',
    flexGrow: 1,
    overflow: 'hidden',
  },
  clientContentContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '30px 0px',
    width: '100%',
    height: `calc(100vh - ${theme.pageContentOffset}px)`,
    overflow: 'auto',
    background: theme.backgroundColor,
  },
  clientContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 850,
    gap: 30,
  },
}))

const ClientDetail = (props) => {
  const classes = useStyles()
  const refArray = useRef({})

  const {
    client,
    refresh,
    shouldRefresh,
    useResetClientData,
    getModuleAccess,
    moduleAccess,
    isLoading,
  } = useClientSetting()
  const isOfflineClient = client?.inviteeStatus === CLIENT_INVITATION_STATUS.OFFLINE

  const datevExportAccess = moduleAccess?.[SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DATEV]
  const documentExportAccess = moduleAccess?.[SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_DOCUMENTS]
  const exportListAccess = moduleAccess?.[SUBSCRIPTION_MODULES.FINANCEFORM_EXPORT_LIST]
  const taxFormSettingsAccess = moduleAccess?.[SUBSCRIPTION_MODULES.TAXFORM_TAX_FORM_SETTINGS]

  useEffect(() => {
    refresh()
  }, [])

  useWebSocketEvent(EventTypes.COLLABORATION_UPDATED, ({ data }) => {
    if (data.id === client?.id) {
      refresh()
    }
  })
  useWebSocketEvent(EventTypes.COLLABORATION_STATUS_CHANGED, ({ data }) => {
    if (data.id === client?.id) {
      refresh()
    }
  })

  useEffect(() => {
    getModuleAccess(props.id)
  }, [])

  const addRef = (el) => addElementToRefs(el, refArray)
  const ClientOverviewWidget = useMemo(() => <ClientOverview ref={addRef} client={client} />, [client])
  const ClientTodoWidget = useMemo(() => <ClientTodo ref={addRef} client={client} />, [client])
  const ClientESigningWidget = useMemo(() => <ClientESigning ref={addRef} client={client} />, [client])
  const ClientTaxFormWidget = useMemo(() => <ClientTaxForm refresh={refresh} shouldRefresh={shouldRefresh} ref={addRef} client={client} />, [client])
  const ClientDatevExportWidget = useMemo(() => <ClientDatevExport ref={addRef} client={client} />, [client])
  const ClientDocumentExportWidget = useMemo(() => <ClientDocumentExport ref={addRef} client={client} />, [client])
  const ClientCreatedExportsWidget = useMemo(() => <ClientCreatedExports ref={addRef} client={client} />, [client])

  useScrollToJumpmarks({ refArray })

  useResetClientData()

  const createSubmenuItems = (sectionObject, textsToUse) => {
    return Object.keys(sectionObject).map((item) => ({
      link: item,
      headline: textsToUse[item]?.headline,
      text: textsToUse[item]?.text,
      active: false,
    }))
  }

  const withAccessRights = (jumpMarkLinks) => {
    const links = { ...jumpMarkLinks }

    if (isOfflineClient) {
      delete links?.taxForm
      delete links?.taxAccountantExport
      delete links?.documentExport
      delete links?.createdExports
    } else {
      if (!taxFormSettingsAccess) delete links?.taxForm
      if (!datevExportAccess) delete links?.taxAccountantExport
      if (!documentExportAccess) delete links?.documentExport
      if (!exportListAccess) delete links?.createdExports
    }

    return links
  }

  return (
    <div className={classes.clientDetailWrapper}>
      <Loader visible={isLoading} withOpacity text={lang.generics.loading} />
      <Topbar
        title={client?.companyName}
        backButtonUrl='/clients'
        variant='white'
      />
      <div className={classes.clientContentWrapper}>
        {<Subnavigation
          items={createSubmenuItems(withAccessRights(jumpMarkLinks), lang.clientDetail.jumpMarkLinkTexts)}
          refList={refArray.current}
        />}
        <div className={classes.clientContentContainer}>
          <div className={classes.clientContent}>
            <AccountSettingProvider collaborationId={client?.id}>
              {ClientOverviewWidget}
              {ClientTodoWidget}
              {ClientESigningWidget}
              {!isOfflineClient && (
                <>
                  {taxFormSettingsAccess && ClientTaxFormWidget}
                  {datevExportAccess && ClientDatevExportWidget}
                  {documentExportAccess && ClientDocumentExportWidget}
                  {exportListAccess && ClientCreatedExportsWidget}
                </>
              )}
            </AccountSettingProvider>
          </div>
        </div>
      </div>
    </div>
  )
}

const fetchData = ({ params }) => {
  dispatch(fetchClientData(params.id))
  return Promise.resolve(params)
}

export default withAsyncData(ClientDetail, lang.clientDetail.title, fetchData)
